import React, { useState, useMemo, useEffect, useCallback } from 'react';
import gauraJson from '../../assets/gaura.json';

// Static GeoJSON data
const geoJsonData = gauraJson;

const SVGGeoJSONMap = () => {
  const [hoveredFeature, setHoveredFeature] = useState<any>(null);
  const [selectedFeature, setSelectedFeature] = useState<any>(null);
  const [tooltip, setTooltip] = useState<{ show: boolean; x: number; y: number; content: any }>({ show: false, x: 0, y: 0, content: null });
  
  // State untuk menyimpan data yang bisa diubah
  const [featuresData, setFeaturesData] = useState<any[]>(geoJsonData.features);
  
  // State untuk JSON input
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [showJsonPanel, setShowJsonPanel] = useState(false);
  
  // Zoom and Pan states
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Calculate bounds and projection
  const { projectedFeatures } = useMemo(() => {
    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    // Find bounding box
    featuresData.forEach(feature => {
      feature.geometry.coordinates[0].forEach((coord: any) => {
        minLng = Math.min(minLng, coord[0]);
        maxLng = Math.max(maxLng, coord[0]);
        minLat = Math.min(minLat, coord[1]);
        maxLat = Math.max(maxLat, coord[1]);
      });
    });

    const padding = 0.0001; // Small padding
    const bounds = {
      minLng: minLng - padding,
      maxLng: maxLng + padding,
      minLat: minLat - padding,
      maxLat: maxLat + padding
    };

    // Project coordinates to SVG space
    const svgWidth = 800;
    const svgHeight = 600;
    
    const projectedFeatures = featuresData.map(feature => {
      const projectedCoords = feature.geometry.coordinates[0].map((coord: any) => {
        const x = ((coord[0] - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * svgWidth;
        const y = svgHeight - ((coord[1] - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * svgHeight;
        return [x, y];
      });

      return {
        ...feature,
        projectedCoords,
        pathString: `M ${projectedCoords.map((coord: any) => coord.join(',')).join(' L ')} Z`
      };
    });

    return { projectedFeatures };
  }, [featuresData]);

  // Get color based on status
  const getColor = useCallback((status: string) => {
    if (status === "Available") return '#0ff207'; // Green
    if (status === "Utama") return '#9dede5'; // Light cyan
    if (status === "Sold") return '#ff4757'; // Red
    return '#FF9800'; // Orange (default)
  }, []);

  // Function to change status
  const changeStatus = useCallback((featureId: any, newStatus: string) => {
    setFeaturesData(prevFeatures => 
      prevFeatures.map(feature => 
        feature.properties.id === featureId 
          ? {
              ...feature,
              properties: {
                ...feature.properties,
                status: newStatus
              }
            }
          : feature
      )
    );
    
    // Update selected feature if it's currently selected
    if (selectedFeature && selectedFeature.id === featureId) {
      setSelectedFeature((prev: any) => ({
        ...prev,
        status: newStatus
      }));
    }
  }, [selectedFeature]);

  // Function to update status by name
  const changeStatusByName = useCallback((featureName: string, newStatus: string) => {
    setFeaturesData(prevFeatures => 
      prevFeatures.map(feature => 
        feature.properties.Name === featureName 
          ? {
              ...feature,
              properties: {
                ...feature.properties,
                status: newStatus
              }
            }
          : feature
      )
    );
    
    // Update selected feature if it's currently selected
    if (selectedFeature && selectedFeature.Name === featureName) {
      setSelectedFeature((prev: any) => ({
        ...prev,
        status: newStatus
      }));
    }
  }, [selectedFeature]);

  // Function to process JSON input and update multiple statuses
  const applyJsonUpdates = useCallback(() => {
    setJsonError('');
    
    if (!jsonInput.trim()) {
      setJsonError('Input JSON tidak boleh kosong');
      return;
    }

    try {
      const updates = JSON.parse(jsonInput);
      
      if (!Array.isArray(updates)) {
        setJsonError('JSON harus berupa array. Contoh: [{"Name":"G7/01", "status": "Sold"}]');
        return;
      }

      const validStatuses = ['Available', 'Sold', 'Utama'];
      const errors: string[] = [];
      const successUpdates: any[] = [];

      // Validate each update
      updates.forEach((update, index) => {
        if (!update.Name) {
          errors.push(`Item ${index + 1}: Property "Name" diperlukan`);
          return;
        }
        
        if (!update.status) {
          errors.push(`Item ${index + 1}: Property "status" diperlukan`);
          return;
        }

        if (!validStatuses.includes(update.status)) {
          errors.push(`Item ${index + 1}: Status "${update.status}" tidak valid. Gunakan: ${validStatuses.join(', ')}`);
          return;
        }

        // Check if the name exists in features
        const featureExists = featuresData.find(f => f.properties.Name === update.Name);
        if (!featureExists) {
          errors.push(`Item ${index + 1}: Area "${update.Name}" tidak ditemukan`);
          return;
        }

        // Check if trying to change Utama status
        if (featureExists.properties.status === 'Utama' && update.status !== 'Utama') {
          errors.push(`Item ${index + 1}: Area "${update.Name}" adalah Area Utama dan tidak dapat diubah statusnya`);
          return;
        }

        successUpdates.push(update);
      });

      if (errors.length > 0) {
        setJsonError(errors.join('\n'));
        return;
      }

      // Apply all valid updates
      successUpdates.forEach(update => {
        changeStatusByName(update.Name, update.status);
      });

      // Clear input and show success
      setJsonInput('');
      setJsonError('');
      alert(`✅ Berhasil mengupdate ${successUpdates.length} area!`);
      
    } catch (error: any) {
      setJsonError(`Format JSON tidak valid: ${error.message}`);
    }
  }, [jsonInput, featuresData, changeStatusByName]);

  // Toggle status between Available and Sold
  const toggleStatus = useCallback((featureId: any, currentStatus: string) => {
    if (currentStatus === "Available") {
      changeStatus(featureId, "Sold");
    } else if (currentStatus === "Sold") {
      changeStatus(featureId, "Available");
    }
    // Area Utama tidak bisa diubah statusnya
  }, [changeStatus]);

  // Get statistics
  const getStats = useCallback(() => {
    const available = featuresData.filter(f => f.properties.status === 'Available').length;
    const sold = featuresData.filter(f => f.properties.status === 'Sold').length;
    const utama = featuresData.filter(f => f.properties.status === 'Utama').length;
    const sellableTotal = available + sold;
    const soldPercentage = sellableTotal > 0 ? ((sold / sellableTotal) * 100).toFixed(1) : '0';
    
    return { available, sold, utama, sellableTotal, soldPercentage };
  }, [featuresData]);

  const stats = getStats();

  // Export functions
  const exportToJSON = useCallback(() => {
    const dataToExport = {
      timestamp: new Date().toISOString(),
      summary: stats,
      features: featuresData.map(f => ({
        id: f.properties.id,
        name: f.properties.Name,
        jenis: f.properties.jenis,
        status: f.properties.status,
        value: f.properties.value,
        description: f.properties.description
      }))
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gaura-plot-status-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [stats, featuresData]);

  const exportToCSV = useCallback(() => {
    const headers = ['ID', 'Name', 'Jenis', 'Status', 'Value', 'Description'];
    const csvData = featuresData.map(f => [
      f.properties.id,
      f.properties.Name,
      f.properties.jenis,
      f.properties.status,
      f.properties.value,
      f.properties.description || ''
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gaura-plot-status-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [featuresData]);

  // Bulk actions
  const resetAllToAvailable = useCallback(() => {
    setFeaturesData(prevFeatures => 
      prevFeatures.map(feature => 
        feature.properties.status !== "Utama" 
          ? {
              ...feature,
              properties: {
                ...feature.properties,
                status: "Available"
              }
            }
          : feature
      )
    );
    
    // Reset selected feature if needed
    if (selectedFeature && selectedFeature.status !== "Utama") {
      setSelectedFeature((prev: any) => ({
        ...prev,
        status: "Available"
      }));
    }
  }, [selectedFeature]);

  // Zoom and Pan functions
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.5, 10));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.5, 0.5));
  }, []);

  const handleResetView = useCallback(() => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  }, []);

  // Mouse wheel zoom
  const handleWheel = useCallback((event: any) => {
    event.preventDefault();
    
    if (event.deltaY < 0) {
      // Zoom in
      setZoom(prev => Math.min(prev * 1.2, 10));
    } else {
      // Zoom out
      setZoom(prev => Math.max(prev / 1.2, 0.5));
    }
  }, []);

  // Mouse drag for panning
  const handleMouseDown = useCallback((event: any) => {
    if (event.button === 0) { // Left mouse button
      setIsDragging(true);
      setDragStart({
        x: event.clientX - panX,
        y: event.clientY - panY
      });
      event.preventDefault();
    }
  }, [panX, panY]);

  const handleMouseMove = useCallback((event: any) => {
    if (isDragging) {
      setPanX(event.clientX - dragStart.x);
      setPanY(event.clientY - dragStart.y);
      event.preventDefault();
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch support for mobile
  const handleTouchStart = useCallback((event: any) => {
    if (event.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: event.touches[0].clientX - panX,
        y: event.touches[0].clientY - panY
      });
      event.preventDefault();
    }
  }, [panX, panY]);

  const handleTouchMove = useCallback((event: any) => {
    if (isDragging && event.touches.length === 1) {
      setPanX(event.touches[0].clientX - dragStart.x);
      setPanY(event.touches[0].clientY - dragStart.y);
      event.preventDefault();
    }
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Calculate viewBox based on zoom and pan
  const getViewBox = useCallback(() => {
    const baseWidth = 800;
    const baseHeight = 600;
    const width = baseWidth / zoom;
    const height = baseHeight / zoom;
    const x = (baseWidth - width) / 2 - (panX / zoom);
    const y = (baseHeight - height) / 2 - (panY / zoom);
    return `${x} ${y} ${width} ${height}`;
  }, [zoom, panX, panY]);

  // Handle mouse events
  const handleMouseEnter = useCallback((feature: any, event: any) => {
    setHoveredFeature(feature.properties.id);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      x: event.clientX - rect.left + 10,
      y: event.clientY - rect.top - 10,
      content: feature
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredFeature(null);
    setTooltip({ show: false, x: 0, y: 0, content: '' });
  }, []);

  const handleClick = useCallback((feature: any) => {
    setSelectedFeature(feature.properties);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.target.tagName.toLowerCase() === 'input') return; // Don't interfere with input fields
      
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          handleZoomIn();
          break;
        case '-':
          event.preventDefault();
          handleZoomOut();
          break;
        case '0':
          event.preventDefault();
          handleResetView();
          break;
        case 'ArrowUp':
          event.preventDefault();
          setPanY(prev => prev + 50);
          break;
        case 'ArrowDown':
          event.preventDefault();
          setPanY(prev => prev - 50);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          setPanX(prev => prev + 50);
          break;
        case 'ArrowRight':
          event.preventDefault();
          setPanX(prev => prev - 50);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleResetView]);

  return (
    <div style={{ width: '100%', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '15px',
        borderRadius: '8px 8px 0 0',
        marginBottom: '0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '18px' }}>
              🗺️ Peta SVG Property Area Gaura
            </h3>
            <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
              Native SVG Rendering | Total: {featuresData.length} area
            </p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '12px' }}>
            <div>🟢 Available: {stats.available}</div>
            <div>🔴 Sold: {stats.sold}</div>
            <div style={{ color: '#9dede5' }}>🏠 Area Utama: {stats.utama}</div>
          </div>
        </div>
      </div>

      {/* Admin Control Panel */}
      <div style={{
        padding: '12px 15px',
        backgroundColor: '#34495e',
        color: 'white',
        borderTop: '1px solid #2c3e50',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', fontSize: '14px' }}>
          <span>
            📊 Sales Progress: <strong>{stats.soldPercentage}%</strong> 
            ({stats.sold}/{stats.sellableTotal} plots)
          </span>
          <div style={{
            width: '100px',
            height: '8px',
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${stats.soldPercentage}%`,
              height: '100%',
              backgroundColor: parseFloat(stats.soldPercentage) > 70 ? '#e74c3c' : 
                             parseFloat(stats.soldPercentage) > 40 ? '#f39c12' : '#27ae60',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowJsonPanel(!showJsonPanel)}
            style={{
              padding: '6px 12px',
              backgroundColor: showJsonPanel ? '#8e44ad' : '#9b59b6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            title="Open JSON bulk update panel"
          >
            📝 JSON Update
          </button>
          <button
            onClick={exportToJSON}
            style={{
              padding: '6px 12px',
              backgroundColor: '#9b59b6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            title="Export status to JSON file"
          >
            📄 JSON
          </button>
          <button
            onClick={exportToCSV}
            style={{
              padding: '6px 12px',
              backgroundColor: '#16a085',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            title="Export status to CSV file"
          >
            📊 CSV
          </button>
          <button
            onClick={resetAllToAvailable}
            style={{
              padding: '6px 12px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            title="Reset all plots to Available status"
          >
            🔄 Reset All
          </button>
          <button
            onClick={() => {
              const availablePlots = featuresData.filter(f => f.properties.status === 'Available');
              if (availablePlots.length > 0) {
                availablePlots.forEach(plot => changeStatus(plot.properties.id, 'Sold'));
              }
            }}
            disabled={stats.available === 0}
            style={{
              padding: '6px 12px',
              backgroundColor: stats.available === 0 ? '#7f8c8d' : '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: stats.available === 0 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              opacity: stats.available === 0 ? 0.6 : 1
            }}
            title="Mark all available plots as sold"
          >
            🔴 Sell All ({stats.available})
          </button>
        </div>
      </div>

      {/* JSON Bulk Update Panel */}
      {showJsonPanel && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderLeft: '4px solid #9b59b6',
          margin: '0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>📝 JSON Bulk Status Update</h4>
            <button
              onClick={() => setShowJsonPanel(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#7f8c8d'
              }}
              title="Close panel"
            >
              ✖️
            </button>
          </div>
          
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#2c3e50' }}>
              Input JSON Array (contoh format):
            </label>
            <div style={{
              backgroundColor: '#2c3e50',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'monospace',
              marginBottom: '10px'
            }}>
            </div>
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder='Masukkan JSON array untuk update multiple status. Contoh:
[\n  {"Name":"G7/01", "status": "Sold"},\n  {"Name":"G7/02", "status": "Available"}\n]'
              style={{
                width: '100%',
                height: '120px',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace',
                resize: 'vertical'
              }}
            />
          </div>

          {jsonError && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '10px',
              fontSize: '12px',
              whiteSpace: 'pre-line',
              border: '1px solid #f5c6cb'
            }}>
              ❌ Error: {jsonError}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={applyJsonUpdates}
              style={{
                padding: '8px 16px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
              title="Apply JSON updates"
            >
              ✅ Apply Updates
            </button>
            
            <button
              onClick={() => {
                setJsonInput('');
                setJsonError('');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              title="Clear input"
            >
              🗑️ Clear
            </button>

            <button
              onClick={() => {
                const exampleJson = [
                  {"Name": "G7/01", "status": "Sold"},
                  {"Name": "G7/02", "status": "Available"},
                  {"Name": "G7/03", "status": "Sold"}
                ];
                setJsonInput(JSON.stringify(exampleJson, null, 2));
                setJsonError('');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              title="Load example JSON"
            >
              📋 Example
            </button>
          </div>

          <div style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#d1ecf1',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#0c5460'
          }}>
            <strong>📌 Petunjuk:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>Format: Array JSON dengan property "Name" dan "status"</li>
              <li>Status valid: "Available", "Sold", "Utama"</li>
              <li>Name harus sesuai dengan nama area yang ada (case-sensitive)</li>
              <li>Area Utama tidak dapat diubah statusnya</li>
              <li>Klik "Example" untuk melihat contoh format</li>
            </ul>
          </div>
        </div>
      )}

      {/* SVG Map Container */}
      <div style={{ 
        position: 'relative',
        border: '1px solid #ddd',
        borderTop: 'none',
        backgroundColor: '#f8f9fa',
        overflow: 'hidden'
      }}>
        {/* Zoom Controls */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: '5px'
        }}>
          <button
            onClick={handleZoomIn}
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            title="Zoom In"
          >
            +
          </button>
          <button
            onClick={handleZoomOut}
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            title="Zoom Out"
          >
            −
          </button>
          <button
            onClick={handleResetView}
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            title="Reset View"
          >
            ⌂
          </button>
        </div>

        {/* Zoom Level Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          fontFamily: 'monospace'
        }}>
          Zoom: {zoom.toFixed(1)}x
        </div>

        <svg
          width="100%"
          height="600"
          viewBox={getViewBox()}
          style={{ 
            display: 'block',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none'
          }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Background */}
          <rect width="800" height="600" fill="#f0f8ff" />
          
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0e0e0" strokeWidth="1" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="800" height="600" fill="url(#grid)" />
          
          {/* Render polygons */}
          {projectedFeatures.map((feature, index) => {
            const isHovered = hoveredFeature === feature.properties.id;
            const color = getColor(feature.properties.status || "Available");
            
            return (
              <g key={feature.properties.id || index}>
                <path
                  d={feature.pathString}
                  fill={color}
                  stroke={isHovered ? '#ffffff' : '#2c3e50'}
                  strokeWidth={isHovered ? 3 : 2}
                  opacity={isHovered ? 0.9 : 0.8}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => handleMouseEnter(feature, e)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleClick(feature)}
                />
                
                {/* Text labels */}
                {feature.projectedCoords.length > 0 && (
                  <text
                    x={feature.projectedCoords.reduce((sum: number, coord: any) => sum + coord[0], 0) / feature.projectedCoords.length}
                    y={feature.projectedCoords.reduce((sum: number, coord: any) => sum + coord[1], 0) / feature.projectedCoords.length}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="6"
                    fontWeight="bold"
                    fill="#2c3e50"
                    stroke="white"
                    strokeWidth="0.5"
                    style={{ pointerEvents: 'none' }}
                  >
                    {feature.properties.Name}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip.show && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
              zIndex: 1000,
              whiteSpace: 'nowrap'
            }}
          >
            <div><strong>{tooltip.content.properties?.Name}</strong></div>
            <div>Jenis: {tooltip.content.properties?.jenis}</div>
            <div>Status: <span style={{ 
              color: tooltip.content.properties?.status === 'Available' ? '#0ff207' : 
                     tooltip.content.properties?.status === 'Sold' ? '#ff4757' : '#9dede5'
            }}>
              {tooltip.content.properties?.status}
            </span></div>
            <div>Nilai: {tooltip.content.properties?.value}</div>
            {tooltip.content.properties?.description && tooltip.content.properties.description !== 'null' && (
              <div>Estimasi: Rp {parseInt(tooltip.content.properties.description).toLocaleString('id-ID')}</div>
            )}
          </div>
        )}
      </div>

      {/* Feature Info Panel */}
      {selectedFeature && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#ecf0f1',
          borderRadius: '8px',
          border: '1px solid #bdc3c7'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>
            📍 Detail Area: {selectedFeature.Name}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '15px' }}>
            <div><strong>Jenis:</strong> {selectedFeature.jenis}</div>
            <div><strong>Status:</strong> 
              <span style={{ 
                marginLeft: '5px',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: selectedFeature.status === 'Available' ? '#0ff207' : 
                               selectedFeature.status === 'Sold' ? '#ff4757' : '#9dede5'
              }}>
                {selectedFeature.status}
              </span>
            </div>
            <div><strong>Nilai:</strong> {selectedFeature.value}</div>
            <div><strong>ID:</strong> {selectedFeature.id}</div>
            {selectedFeature.description && selectedFeature.description !== 'null' && (
              <div><strong>Nilai Estimasi:</strong> Rp {parseInt(selectedFeature.description).toLocaleString('id-ID')}</div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {selectedFeature.status !== 'Utama' && (
              <button 
                onClick={() => toggleStatus(selectedFeature.id, selectedFeature.status)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: selectedFeature.status === 'Available' ? '#e74c3c' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                title={selectedFeature.status === 'Available' ? 'Mark as Sold' : 'Mark as Available'}
              >
                {selectedFeature.status === 'Available' ? '🔴 Mark as Sold' : '🟢 Mark as Available'}
              </button>
            )}
            
            <button 
              onClick={() => setSelectedFeature(null)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✖️ Tutup
            </button>
          </div>
          
          {selectedFeature.status === 'Utama' && (
            <div style={{
              marginTop: '10px',
              padding: '8px 12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              color: '#856404',
              fontSize: '12px'
            }}>
              ℹ️ Area Utama tidak dapat diubah statusnya
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{
        marginTop: '15px',
        padding: '15px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #ddd',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#2c3e50' }}>🎨 Informasi Visualisasi & Status</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', fontSize: '14px' }}>
          <div>
            <strong>Warna berdasarkan status:</strong>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
              <div style={{ width: '20px', height: '15px', backgroundColor: '#0ff207', marginRight: '5px', border: '1px solid #ccc' }}></div>
              <span>🟢 Available (Tersedia)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
              <div style={{ width: '20px', height: '15px', backgroundColor: '#ff4757', marginRight: '5px', border: '1px solid #ccc' }}></div>
              <span>🔴 Sold (Terjual)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
              <div style={{ width: '20px', height: '15px', backgroundColor: '#9dede5', marginRight: '5px', border: '1px solid #ccc' }}></div>
              <span>🏠 Area Utama</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '3px' }}>
              <div style={{ width: '20px', height: '15px', backgroundColor: '#FF9800', marginRight: '5px', border: '1px solid #ccc' }}></div>
              <span>🟠 Status Lainnya</span>
            </div>
          </div>
          <div>
            <strong>Fitur Interaktif:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>🖱️ Hover untuk tooltip info</li>
              <li>👆 Click untuk detail area</li>
              <li>🔍 Mouse wheel untuk zoom</li>
              <li>✋ Drag untuk pan/geser</li>
              <li>⌂ Tombol reset view</li>
              <li>🔄 Toggle status Available/Sold</li>
            </ul>
          </div>
          <div>
            <strong>Manajemen Status:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>✅ Click area untuk lihat detail</li>
              <li>🔴 Button "Mark as Sold"</li>
              <li>🟢 Button "Mark as Available"</li>
              <li>🏠 Area Utama tidak bisa diubah</li>
              <li>📊 Counter status di header</li>
            </ul>
          </div>
          <div>
            <strong>Admin Features:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>📊 Real-time sales progress</li>
              <li>🔄 Bulk reset to Available</li>
              <li>🔴 Bulk mark as Sold</li>
              <li>📝 JSON bulk status update</li>
              <li>📄 Export to JSON/CSV</li>
              <li>📈 Statistics dashboard</li>
            </ul>
          </div>
        </div>
        
        <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', fontSize: '14px' }}>
          <div>
            <strong>Zoom & Navigation:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>➕ Tombol + untuk zoom in</li>
              <li>➖ Tombol - untuk zoom out</li>
              <li>🔄 Mouse wheel scroll</li>
              <li>📱 Touch gestures support</li>
              <li>🎯 Auto-fit pada reset</li>
            </ul>
          </div>
          <div>
            <strong>Data Export:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>📄 JSON format dengan timestamp</li>
              <li>📊 CSV untuk spreadsheet</li>
              <li>📈 Summary statistics included</li>
              <li>🗓️ Auto-dated filename</li>
              <li>💾 Download ke local storage</li>
            </ul>
          </div>
          <div>
            <strong>JSON Bulk Update:</strong>
            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
              <li>📝 Update multiple plots at once</li>
              <li>📋 JSON format validation</li>
              <li>✅ Real-time error checking</li>
              <li>🏠 Utama area protection</li>
              <li>📌 Example templates provided</li>
            </ul>
          </div>
        </div>
        
        <div style={{
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          fontSize: '14px'
        }}>
          <strong>📊 Summary Status:</strong>
          <div style={{ display: 'flex', gap: '20px', marginTop: '8px', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#0ff207', borderRadius: '50%', marginRight: '5px' }}></div>
              Available: {stats.available} plot
            </span>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#ff4757', borderRadius: '50%', marginRight: '5px' }}></div>
              Sold: {stats.sold} plot
            </span>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#9dede5', borderRadius: '50%', marginRight: '5px' }}></div>
              Area Utama: {stats.utama} area
            </span>
            <span style={{ 
              marginLeft: 'auto', 
              fontWeight: 'bold', 
              color: '#2c3e50',
              padding: '2px 8px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #ddd'
            }}>
              Sales Rate: {stats.soldPercentage}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SVGGeoJSONMap;
